import React, { useEffect } from 'react'
import { observer } from 'mobx-react-lite'

import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import List from '@mui/material/List'

import {
  shouldShowDate,
  getInsightNotificationTypeValues,
} from '../../core/utils/NotificationUtils'
import {
  type Notification,
  NotificationTypes,
  NotificationTypes as NotificationTypesEnum,
} from '../../core'

import { Loader, useGlobalUiStore, useNotificationStore } from '../../common'

import DateRow from './DateRow'
import NotificationCard from './NotificationCard'
import { NotificationEmptyState } from './NotificationsEmptyState'

export interface NotificationListProps {
  notificationTypes?: NotificationTypes[]
  onClick?: (notification: Notification) => void
  onManageNotifications: () => void
}

const NotificationList: React.FC<NotificationListProps> = ({
  notificationTypes,
  onClick,
  onManageNotifications,
}) => {
  const { isLoading, loadNotifications, markAllAsViewed, recentNotifications } =
    useNotificationStore()
  const { isInitialized } = useGlobalUiStore()

  const handleClick = (notification: Notification) => {
    onClick?.(notification)
  }

  useEffect(() => {
    loadNotifications()
      .then(() => markAllAsViewed())
      .finally()
  }, [])

  const isNotificationTypesInsights = notificationTypes?.includes(NotificationTypesEnum.Insights)

  if (!isInitialized || isLoading) {
    return <Loader />
  }

  const filteredNotifications = recentNotifications
    .slice()
    .sort((a, b) => b.delivered_at - a.delivered_at)
    .filter((notification) =>
      isNotificationTypesInsights
        ? getInsightNotificationTypeValues().has(notification.notification_type)
        : true,
    )

  return (
    <>
      {filteredNotifications.length === 0 && (
        <NotificationEmptyState onManageNotifications={onManageNotifications} />
      )}
      <List sx={{ p: 16 }}>
        {filteredNotifications.map((notification, index) => {
          const showDate = shouldShowDate(index, filteredNotifications)

          return (
            <Box key={notification.guid} sx={{ mb: 12 }}>
              {showDate && (
                <Box sx={{ mb: 12 }}>
                  <DateRow date={notification.delivered_at} />
                  <Divider />
                </Box>
              )}
              <NotificationCard notification={notification} onClick={handleClick} />
            </Box>
          )
        })}
      </List>
    </>
  )
}

export default observer(NotificationList)
